import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BrandLockup } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import GalleryReportCard from "@/components/GalleryReportCard";
import DecodeMiniCard from "@/components/DecodeMiniCard";
import CertificateCard from "@/components/CertificateCard";
import { Award, Heart, ScrollText, Loader2, UserX } from "lucide-react";

export default function PublicProfile() {
  const navigate = useNavigate();
  const { username } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(undefined);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    api.get(`/public/profile/${encodeURIComponent(username)}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, [username]);

  const view = async (item) => {
    setOpen(true); setLoadingDetail(true); setDetail({ item, full: null });
    try { const r = await api.get(`/decode/report/${item.id}`); setDetail({ item, full: r.data }); }
    catch (e) { /* ignore */ } finally { setLoadingDetail(false); }
  };
  const miniData = detail?.full
    ? { input_text: detail.full.input_text, mini: detail.full.mini_decode, rule_output: detail.full.rule_output_json }
    : null;

  return (
    <div className="min-h-screen bg-[#0B0C0E] text-[var(--bone)]">
      <header className="glass border-b border-white/[0.06] px-4 sm:px-6 py-3 flex items-center justify-between sticky top-0 z-30">
        <button onClick={() => navigate("/")} data-testid="public-brand"><BrandLockup /></button>
        {user ? (
          <Button variant="outline" className="glass" onClick={() => navigate("/today")} data-testid="public-open-app">Open app</Button>
        ) : (
          <Button variant="outline" className="glass" onClick={() => navigate("/login")} data-testid="public-sign-in">Sign in</Button>
        )}
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {data === undefined ? (
          <div className="space-y-4"><Skeleton className="h-32 w-full bg-white/[0.04]" /><Skeleton className="h-48 w-full bg-white/[0.04]" /></div>
        ) : data === null ? (
          <div className="litra-card p-10 text-center" data-testid="public-not-found">
            <UserX className="w-8 h-8 mx-auto text-[var(--bone-faint)]" />
            <p className="mt-3 text-[var(--bone-muted)]">We couldn’t find a profile for “{username}”.</p>
            <Button className="mt-4 bg-[var(--primary)] hover:opacity-90" onClick={() => navigate("/")}>Back home</Button>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Header */}
            <div className="litra-card p-6" data-testid="public-profile-header">
              <div className="flex items-center gap-4 flex-wrap">
                <Avatar className="w-16 h-16"><AvatarFallback className="bg-[rgba(20,90,67,0.3)] text-[var(--bone)] font-display text-xl">{(data.profile.username || "U").slice(0, 2).toUpperCase()}</AvatarFallback></Avatar>
                <div className="flex-1 min-w-0">
                  <div className="text-2xl font-display font-semibold">{data.profile.username}</div>
                  <div className="mt-1 flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="green-text border-[rgba(20,90,67,0.5)]" data-testid="public-rank">{data.profile.rank}</Badge>
                    <Badge className="gold-edge gold-text bg-transparent">Level {data.profile.level}</Badge>
                    {data.profile.dominant_archetype && <Badge variant="outline" className="border-[rgba(185,192,204,0.3)]">{data.profile.dominant_archetype}</Badge>}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <Stat value={data.counts.public_decodes} label="public" />
                  <Stat value={data.counts.total_likes} label="likes" />
                  <Stat value={data.counts.certificates} label="badges" />
                </div>
              </div>
              {data.strongest_stat?.label && (
                <div className="mt-4 text-sm text-[var(--bone-muted)]">Strongest signal: <span className="green-text">{data.strongest_stat.label}</span> · {data.profile.xp} XP · {data.profile.streak} day streak</div>
              )}
            </div>

            {/* Certificates */}
            {data.certificates?.length > 0 && (
              <div>
                <div className="text-sm font-display font-semibold flex items-center gap-2 mb-3"><Award className="w-4 h-4 gold-text" />Certificates &amp; Badges</div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="public-certificates">
                  {data.certificates.map((c) => <CertificateCard key={c.id} certificate={c} username={data.profile.username} />)}
                </div>
              </div>
            )}

            {/* Public decodes */}
            <div>
              <div className="text-sm font-display font-semibold flex items-center gap-2 mb-3"><ScrollText className="w-4 h-4 green-text" />Public Decodes</div>
              {data.public_decodes?.length ? (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="public-decodes-grid">
                  {data.public_decodes.map((it) => <GalleryReportCard key={it.id} item={it} onView={view} showAuthor={false} />)}
                </div>
              ) : (
                <div className="litra-card p-8 text-center text-[var(--bone-faint)]" data-testid="public-decodes-empty">This decoder hasn’t shared any public decodes yet.</div>
              )}
            </div>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0F1115] border-white/[0.1] max-w-2xl max-h-[88vh] overflow-auto scrollbar-thin">
          <DialogTitle className="sr-only">Decode detail</DialogTitle>
          {loadingDetail && <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>}
          {!loadingDetail && miniData && <DecodeMiniCard data={miniData} decodeType={detail.item.decode_type} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Stat = ({ value, label }) => (
  <div className="rounded-lg bg-white/[0.03] px-3 py-2 min-w-[56px]">
    <div className="text-lg font-display">{value}</div>
    <div className="mono-chip text-[var(--bone-faint)]">{label}</div>
  </div>
);
